<template>
   <div
      v-swipe="{ swipeMovement: (direction) => takeStep(direction), axis: 'x' }"
      class="portal-carousel relative"
      :class="{ 'no-transition': state.noTransition }"
   >
      <!-- prev button - can either be shown visually or appear only for keyboard users -->
      <div v-if="totalSlides > state.perPageAtCurrentWidth" class="prev-next-holder">
         <div class="relative mx-auto h-0 w-full max-w-3xl">
            <button
               ref="carousel-prev"
               :aria-label="$t('system.show_previous')"
               :disabled="!allowPrev"
               data-cy="carousel-prev"
               :class="{ 'hide-except-focus': !showPrevNext }"
               @click="takeStep(-1)"
            >
               <div
                  class="absolute left-0 top-0 size-full rounded-full border border-grey-20 bg-white"
                  :class="{ 'border-grey-70': allowPrev }"
               ></div>
               <div :class="{ 'opacity-25': !allowPrev }" class="trans-opacity">
                  <div
                     class="direction-arrow relative z-10 ml-5 size-4 border-b-2 border-l-2"
                     style="transform: rotate(45deg)"
                  ></div>
               </div>
            </button>
         </div>
      </div>

      <!-- slides & content -->
      <div
         class="slides-holder relative overflow-hidden"
         :class="[{ 'opacity-0': state.fadeOutSlides }, { 'px-4': showPrevNext }, { 'no-slide': noSlide }]"
         :style="`min-height: ${state.minHeight}px; transition-duration:  ${state.transitionTimeMs / 2500}s;`"
      >
         <div
            v-for="index in totalSlides"
            :key="`${name}-slide-${index - 1}`"
            ref="slideRefs"
            class="slide absolute left-0 top-0"
            :class="[
               {
                  current:
                     index - 1 >= state.currentSlide && index - 1 < state.currentSlide + state.perPageAtCurrentWidth,
               },
               { 'px-4': state.perPageAtCurrentWidth > 1 || showPrevNext },
            ]"
            :style="`width: ${100 / state.perPageAtCurrentWidth}%;
                    min-height: ${state.minHeight}px;
                    transition-duration: ${state.hiddenSlide === index - 1 ? 0 : slideTransitionTimeSeconds}s;
                    transition-timing-function: ${state.transitionEase};
                    transform: translateX(${positions[index - 1].x}%);
                    opacity: ${positions[index - 1].opacity}`"
         >
            <transition name="caro-fade">
               <div
                  v-show="positions[index - 1].show || state.showAll"
                  class="relative h-full"
                  :style="`min-height: ${state.minHeight}px;`"
               >
                  <slot :name="`slot-${index - 1}`"></slot>
               </div>
            </transition>
         </div>
      </div>

      <!-- next button - can either be shown visually or appear only for keyboard users -->
      <div v-if="totalSlides > state.perPageAtCurrentWidth" class="prev-next-holder">
         <div class="relative mx-auto flex h-0 w-full max-w-3xl justify-end">
            <button
               ref="carousel-next"
               :disabled="!allowNext"
               :aria-label="`${$t('system.show_next')} ${!allowNext ? $t('system.disabled') : ''}`"
               data-cy="carousel-next"
               :class="{ 'hide-except-focus': !showPrevNext }"
               @click="takeStep(1)"
            >
               <div
                  class="absolute left-0 top-0 size-full rounded-full border border-grey-20 bg-white"
                  :class="{ 'border-grey-70': allowNext }"
               ></div>
               <div :class="{ 'opacity-25': !allowNext }" class="trans-opacity">
                  <div
                     class="direction-arrow relative z-10 -ml-4 size-4 border-r-2 border-t-2"
                     style="transform: rotate(45deg)"
                  ></div>
               </div>
            </button>
         </div>
      </div>
   </div>
</template>

<script setup lang="ts">
import { vSwipe } from "@/directives/swipe";

const emit = defineEmits(["current-slide-change-to"]);

interface Props {
   totalSlides: number;
   // Ensure that refs and keys are unique even if multiple carousels are on a page
   name: string;
   // Allow custom / page specifically designed controls to send instructions to the slider
   updateCurrentSlideTo?: number;
   // How many slides show show across the screen at different screen widths
   perPageAtWidths?: object;
   // When true, the carousel should act like tabs - no slide transforms/movement
   noSlide?: boolean;
   // When true, do not allow 'wrapping' - (where 'next' goes back to 0 after the last slide)
   noWrapAround?: boolean;
   // When true, show the prev / next buttons even while they are not being focused
   showPrevNext?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
   perPageAtWidths: () => {
      return { 0: 1, 576: 1, 768: 1, 992: 1 };
   },
   noSlide: false,
   noWrapAround: false,
});

// state
// ----------------------------------------
const slideRefs = ref(null);

const state = reactive({
   // Used to ensure slides are all the same height as the tallest one
   minHeight: 200,
   // Allow different numbers of slides to appear on screen at different widths
   perPageAtCurrentWidth: 1,
   // An array that can be re-arranged to provide a key for the position each slide should be in
   slides: Array.from(Array(props.totalSlides).keys()),
   // The 'pop-shift' slide (going from e.g. fade out left to fade in right) should not transition across the screen like the others
   hiddenSlide: -1,
   currentSlide: 0,
   transitionTimeMs: 900,
   // jumpingSlides is true (and other navigation prevented) when moving from e.g. slide 0 to slide 6
   jumpingSlides: false,
   // During a jump we need to fade out all the slides for a smooth transition so they don't all flick past too quick
   fadeOutSlides: false,
   // During a jump we may need to move slides without transition once they are hidden
   stopTransitions: false,
   transitionEase: "ease-in-out",
   // For height checks
   showAll: false,
   // Used for testing - apply no transitions
   noTransition: false,
});

// computed
// -----------------------------------------
const slideTransitionTimeSeconds = computed(() => {
   // Prevent transitions when they shouldn't happen by setting transition time to 0
   if (state.stopTransitions || state.noTransition) return 0;
   // transition time should be smaller when we're 'jumping slides' as the transition will have more stages and otherwise seems clunky
   if (state.jumpingSlides) return state.transitionTimeMs / 1500;
   return state.transitionTimeMs / 1000;
});

const okToWrap = computed(() => {
   // If we are allowing wrap (no 'noWrapAround' prop plus we have enough slides) we'll use different positioning than otherwise
   return !props.noWrapAround && props.totalSlides > state.perPageAtCurrentWidth + 2;
});

const wrapPositions = computed(() => {
   // A non-changing set of positions/opacity for each slide if wrapping (going from last slide back to first) is allowed
   if (!okToWrap.value) return [];
   return Array.from(Array(props.totalSlides).keys()).map((position) => {
      if (position === 0) {
         return { x: 0, opacity: 1, show: true };
      } else if (position > state.perPageAtCurrentWidth) {
         // offscreen previous slides position (offscreen left)
         return { x: -100, opacity: 0, show: false };
      } else if (position >= state.perPageAtCurrentWidth) {
         // offscreen next slide position (offscreen right)
         return { x: state.perPageAtCurrentWidth * 100, opacity: 0, show: false };
      } else if (position > 0 && position < state.perPageAtCurrentWidth) {
         // other onscreen slides (not current slide)
         return { x: position * 100, opacity: 1, show: true };
      }
   });
});

const positions = computed(() => {
   // A changing set of positions/opacity for each slide whether wrapping or not
   return Array.from(Array(props.totalSlides).keys()).map((index) => {
      if (okToWrap.value) {
         return wrapPositions.value[state.slides.indexOf(index)];
      } else {
         const onScreen = index >= state.currentSlide && index < state.currentSlide + state.perPageAtCurrentWidth;
         return {
            x: 100 * (index - state.currentSlide),
            opacity: onScreen ? 1 : 0,
            show: onScreen,
         };
      }
   });
});

const allowNext = computed(() => {
   // Don't allow if already moving between slides
   if (state.jumpingSlides) return false;
   // Allow if not already moving & slides are allowed to wrap
   if (okToWrap.value) return true;
   // Allow if slides are not allowed to wrap but this is not the last slide
   return state.currentSlide + state.perPageAtCurrentWidth < props.totalSlides;
});

const allowPrev = computed(() => {
   // Don't allow if already moving between slides
   if (state.jumpingSlides) return false;
   // Allow if not already moving & slides are allowed to wrap
   if (okToWrap.value) return true;
   // Allow if slides are not allowed to wrap but this is not the first slide
   return state.currentSlide > 0;
});

// methods
// -----------------------------------------
const nextSlide = function () {
   if (okToWrap.value) {
      // Visually hide the slide which will move from e.g. 'exited right' to 'ready to enter left'
      state.hiddenSlide = state.slides[state.perPageAtCurrentWidth + 1];
      const popShiftSlide = state.slides.shift();
      state.slides.push(popShiftSlide);
      state.currentSlide = state.slides[0];
   } else {
      state.currentSlide += 1;
   }
};

const prevSlide = function () {
   if (okToWrap.value) {
      state.hiddenSlide = state.slides[state.perPageAtCurrentWidth];
      const popShiftSlide = state.slides.pop();
      state.slides.unshift(popShiftSlide);
      state.currentSlide = state.slides[0];
   } else {
      state.currentSlide -= 1;
   }
};

const takeStep = function (direction) {
   // We're double checking that we should be able to go forward / back here because although
   // previous & next buttons will be disabled if we shouldn't be able, the user may still swipe
   const nextOk = okToWrap.value || state.currentSlide + state.perPageAtCurrentWidth < props.totalSlides;
   const prevOk = okToWrap.value || state.currentSlide > 0;

   if (direction === 1 && nextOk) nextSlide();
   if (direction === -1 && prevOk) prevSlide();
   emit("current-slide-change-to", state.currentSlide);
};

const stopTransitionsAndStep = function (direction, steps) {
   state.stopTransitions = true;
   setTimeout(() => {
      // Step through to the last-but-one position ready to animate back in
      for (let s = 0; s < steps; s++) {
         takeStep(direction);
      }
   }, 0);
};

const checkCarouselIsInViablePosition = function () {
   // When going from a smaller to a bigger screen size, it's possible to end up with gaps
   // because you might have been on the last slide, and then gone to a size where there are 2 slides across
   // In this case, adjust the slide positions
   const lastSlideOnPage = state.currentSlide + state.perPageAtCurrentWidth;
   if (lastSlideOnPage > props.totalSlides) {
      // console.info("adjust for screen size change");
      stopTransitionsAndStep(-1, lastSlideOnPage - props.totalSlides);

      setTimeout(() => {
         if (state.stopTransitions) state.stopTransitions = false;
      }, state.transitionTimeMs * 0.25);
   }
};

const endJump = function (direction) {
   // Animate in at single step speed again while opacity returns to normal
   state.transitionEase = "ease-out";
   setTimeout(() => {
      takeStep(direction);

      setTimeout(() => {
         // Fade up slides
         if (state.fadeOutSlides !== undefined) state.fadeOutSlides = false;
      }, state.transitionTimeMs * 0.2);

      setTimeout(() => {
         // Re-allow navigation
         if (state.jumpingSlides !== undefined && state.transitionEase !== undefined) {
            state.jumpingSlides = false;
            state.transitionEase = "ease-in-out";
         }
      }, state.transitionTimeMs);
   }, 0);
};

const jumpToSlide = function (direction, steps) {
   // Start the transition with a standard slow slide out (disallow navigation while we have timeouts occuring)
   state.jumpingSlides = true;
   state.fadeOutSlides = true;
   state.transitionEase = "ease-in";
   takeStep(direction);

   setTimeout(() => {
      // Jump to almost where we need to be (unless we're there already.) [Ensure context is still correct first.]
      if (endJump !== undefined && stopTransitionsAndStep !== undefined) {
         if (steps > 2) {
            stopTransitionsAndStep(direction, steps - 2);

            setTimeout(() => {
               // Allow time for stopTransitionsAndStep to complete, and then call endJump
               if (endJump !== undefined && state.stopTransitions !== undefined) {
                  state.stopTransitions = false;
                  endJump(direction);
               }
            }, state.transitionTimeMs * 0.1);
         } else {
            endJump(direction);
         }
      }
   }, state.transitionTimeMs * 0.35);
};

const getPerPageAtNewWidth = function () {
   // How many slides should currently be showing alongside each other on a 'page'
   const previousPerPage = state.perPageAtCurrentWidth;
   const w = window.innerWidth;
   const categoriesSmallerThanW = Object.keys(props.perPageAtWidths).filter((wCategory) => parseInt(wCategory) <= w);
   const thisWidthCategory = categoriesSmallerThanW[categoriesSmallerThanW.length - 1];
   state.perPageAtCurrentWidth = props.perPageAtWidths[thisWidthCategory];

   if (state.perPageAtCurrentWidth > previousPerPage && !okToWrap.value) checkCarouselIsInViablePosition();
};

const getAllSlideHeights = async function () {
   // On mount & on screen-resize, find the largest slide to make sure all slides have matching heights
   // Slides must be showing to get their heights correctly
   state.showAll = true;
   // Start with a smaller height so that slide heights are not being affected by the current minHeight
   state.minHeight = 200;

   await nextTick();

   // Cycle through each slide and record the tallest as 'tempMinHeight'
   let tempMinHeight = 200;
   for (let i = 0; i < state.slides.length; i += 1) {
      const thisContent = slideRefs.value[i];
      if (thisContent?.clientHeight && thisContent.clientHeight > tempMinHeight)
         tempMinHeight = thisContent.clientHeight;
   }
   // Set the new minumum height
   state.minHeight = tempMinHeight;
   // console.log(tempMinHeight);

   await nextTick();
   state.showAll = false;
};

const areaResized = function () {
   getAllSlideHeights();
   getPerPageAtNewWidth();
};

// lifecycle methods
// -----------------------------------------
onMounted(() => {
   // Apply information coming from the parent about not starting on slide 0
   if (props.updateCurrentSlideTo) state.currentSlide = props.updateCurrentSlideTo;
   // Add resize event listener and immediately call the resize functions once
   window.addEventListener("resize", areaResized);
   areaResized();
});

onUnmounted(() => {
   window.removeEventListener("resize", areaResized);
});

watch(
   () => props.updateCurrentSlideTo,
   (newVal, prevVal) => {
      // Watch for an instruction from the parent to change the current slide
      // This allows us to have custom slide navigations outside the carousel (e.g. landing page)
      if (newVal !== null && newVal !== state.currentSlide && !state.jumpingSlides) {
         const diff = newVal - state.currentSlide;
         const direction = diff > 0 ? 1 : -1;
         const steps = diff > 0 ? diff : diff * -1;
         // console.log(`update ${state.currentSlide} to ${newVal} (${direction * steps} steps)`);

         if (props.noSlide) {
            // If there's no slide transition, we can jump directly to the right slide
            stopTransitionsAndStep(direction, steps);
         } else {
            // Otherwise we want to animate in stages
            // Which direction should we be going, and how many steps to get there?
            if (steps === 1) takeStep(direction);
            else jumpToSlide(direction, steps);
         }
      }
   }
);
</script>

<style scoped>
/* We're setting transition time directly in styles so that this will always be in sync */
.slides-holder {
   transition-property: opacity;
   transition-timing-function: ease-in-out;
}

.slide {
   transition-property: transform, opacity;
}

/* tab style no-slide version - fade in & out */
.no-slide .slide {
   transform: translateX(0) !important;
   transition-property: opacity;
   transition-duration: 0.45s !important;
}

.no-slide .slide.current {
   z-index: 2;
   transition-delay: 0.45s;
}

/* reduced motion - remove transitions */
.no-transition .slide,
.no-transition .slides-holder,
.no-transition .caro-fade-enter-active,
.no-transition .caro-fade-leave-active {
   transition-property: none;
}

@media (prefers-reduced-motion: reduce) {
   .slide {
      transition-property: none;
   }
}

.prev-next-holder {
   @apply z-20 absolute w-full;
   height: 0;
   top: 50%;
   transform: translateY(-50%);
   left: -2rem;
   width: calc(100% + 4rem);
}

.prev-next-holder.fixed {
   position: fixed;
   left: -1rem;
   width: calc(100% + 2rem);
}

@media (min-width: 1200px) {
   .prev-next-holder:not(.fixed) {
      left: -3.5rem;
      width: calc(100% + 7rem);
   }
}

@media (min-width: 1350px) {
   .prev-next-holder:not(.fixed) {
      left: -5rem;
      width: calc(100% + 10rem);
   }
}

@media (max-width: 449px) {
   .prev-next-holder {
      left: -3rem;
      width: calc(100% + 6rem);
   }

   .prev-next-holder.fixed {
      left: -1.5rem;
      width: calc(100% + 3rem);
   }
}

.prev-next-holder button {
   width: 4rem;
   height: 4rem;
   top: -2rem;
   @apply rounded-full flex items-center justify-center relative;
}

.hide-except-focus:not(:focus) {
   opacity: 0;
}

.caro-fade-enter-active {
   transition: opacity 0.45s ease-in-out 0.45s;
}

.caro-fade-leave-active {
   transition: opacity 0.45s ease-in-out;
}

.caro-fade-leave-to,
.caro-fade-enter,
.caro-fade-enter-from {
   opacity: 0;
}
</style>
